import { Box, chakra, Flex, useClipboard } from '@chakra-ui/react';
import { CheckIcon, ClipboardIcon } from '@heroicons/react/24/outline';
import { Card } from '@main/complykit/components/card';
import { IconButton } from '@main/complykit/components/icon-button';
import { ScrollArea } from '@main/complykit/components/scroll-area';
import { Bleed } from '@main/complykit/components/slots';
import { Status } from '@main/complykit/components/status';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { createColumnHelper, Table } from '../table';

export type DnsRecord = {
  host: string;
  type: string;
  value: string;
  valid: boolean;
};

export const DnsRecordsTable = ({ records }: { records: DnsRecord[] }) => {
  const { t } = useTranslation('ui');
  const columns = useDnsRecordColumns();
  const itemName = useMemo(
    () => ({
      singular: t('domainVerification.dnsTable.record'),
      plural: t('domainVerification.dnsTable.records'),
    }),
    [t],
  );

  return (
    <Card>
      <Bleed>
        <ScrollArea direction="horizontal">
          <Table
            id="domain-verification-dns-records"
            data={records}
            columns={columns}
            itemName={itemName}
            getRowId={(record, index) => `${record.type}-${record.host}-${index}`}
            defaultPageSize={10}
            pageSizeOptions={[10]}
            enableSavingFiltersAndSort={false}
            enableRowSelection={false}
            hideToolbar
            minW="640px"
          />
        </ScrollArea>
      </Bleed>
    </Card>
  );
};

const useDnsRecordColumns = () => {
  const { t } = useTranslation('ui');

  return useMemo(() => {
    const columnHelper = createColumnHelper<DnsRecord>();
    return [
      columnHelper.accessor((record) => (record.valid ? 'valid' : 'pending'), {
        id: 'status',
        header: t('domainVerification.dnsTable.status'),
        size: 100,
        cell: ({ row }) => <DnsRecordStatus valid={row.original.valid} />,
      }),
      columnHelper.accessor((record) => record.type.toUpperCase(), {
        id: 'type',
        header: t('domainVerification.dnsTable.type'),
        size: 80,
        cell: ({ getValue }) => (
          <Flex h="full" w="full" align="center" px={2} py={4}>
            <chakra.code fontSize="sm" lineHeight={DNS_CODE_LINE_HEIGHT}>
              {getValue()}
            </chakra.code>
          </Flex>
        ),
      }),
      columnHelper.accessor((record) => record.host, {
        id: 'host',
        header: t('domainVerification.dnsTable.host'),
        size: 1,
        cell: ({ getValue }) => (
          <CopyableDnsValue
            value={getValue() ?? ''}
            label={t('domainVerification.dnsTable.copyHost')}
          />
        ),
        meta: { sizeUnit: 'fr' },
      }),
      columnHelper.accessor((record) => record.value, {
        id: 'value',
        header: t('domainVerification.dnsTable.value'),
        size: 1.4,
        cell: ({ getValue }) => (
          <CopyableDnsValue
            value={getValue() ?? ''}
            label={t('domainVerification.dnsTable.copyValue')}
          />
        ),
        meta: { sizeUnit: 'fr' },
      }),
    ];
  }, [t]);
};

const DnsRecordStatus = ({ valid }: { valid: boolean }) => {
  const { t } = useTranslation('ui');
  const label = valid
    ? t('domainVerification.dnsTable.valid')
    : t('domainVerification.dnsTable.pending');
  return (
    <Flex h="full" w="full" align="center" px={2} py={4}>
      <Status tone={valid ? 'success' : 'neutral'}>{label}</Status>
    </Flex>
  );
};

const CopyableDnsValue = ({ value, label }: { value: string; label: string }) => (
  <Flex role="group" h="full" w="full" minW={0} align="center" gap={2} px={2} py={4}>
    <Box flex={1} minW={0}>
      <chakra.code
        fontSize="sm"
        lineHeight={DNS_CODE_LINE_HEIGHT}
        whiteSpace="normal"
        overflowWrap="anywhere"
      >
        {value}
      </chakra.code>
    </Box>
    <Flex flexShrink={0} w={COPY_BUTTON_SIZE} justify="center">
      <CopyButton value={value} label={label} />
    </Flex>
  </Flex>
);

const COPY_BUTTON_SIZE = '32px';
const DNS_CODE_LINE_HEIGHT = 4;

const CopyButton = ({ value, label }: { value: string; label: string }) => {
  const { hasCopied, onCopy } = useClipboard(value);

  // The hover-reveal rides the wrapper because the IconButton is closed to it.
  return (
    <Box
      opacity={hasCopied ? 1 : 0}
      transition="opacity 0.15s ease"
      _groupHover={{ opacity: 1 }}
      _focusWithin={{ opacity: 1 }}
    >
      <IconButton icon={hasCopied ? CheckIcon : ClipboardIcon} label={label} onPress={onCopy} />
    </Box>
  );
};
